import { ChangeDetectionStrategy, Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { CollabFileType } from '@common/collab/enums/collab-file-type.enum';
import { WorkspaceFileUserStatus } from '@common/workspace/enums/workspace-file-user-status.enum';
import { WorkspaceFile } from '@common/workspace/models/workspace-file.model';
import { CollabConflictResolutionDialogComponent, CollabConflictResolutionDialogData } from '@portal-core/collab/components/collab-conflict-resolution-dialog/collab-conflict-resolution-dialog.component';
import { CollabService } from '@portal-core/collab/services/collab.service';
import { CurrentService } from '@portal-core/current/services/current.service';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { McValidators } from '@portal-core/forms/services/mc-validators';
import { MaxCommitMessageLength } from '@portal-core/project-files/constants/max-commit-message-length.constant';
import { DialogBase } from '@portal-core/ui/dialog/util/dialog.base';
import { LoadingState } from '@portal-core/util/loading-state';
import { WorkspaceFilesService } from '@portal-core/workspaces/services/workspace-files/workspace-files.service';
import { map, Observable, of, switchMap } from 'rxjs';

export interface CollabCommitDialogData {
  branchName: string;
  clientId: string;
  filePaths: string[];
  licenseId: number;
  projectId: number;
  userId: string;
}

@Component({
  selector: 'mc-collab-commit-dialog',
  templateUrl: './collab-commit-dialog.component.html',
  styleUrls: ['./collab-commit-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CollabCommitDialogComponent extends DialogBase implements OnInit {
  static DialogConfig: MatDialogConfig = {
    width: '64rem',
    minHeight: '37rem',
    autoFocus: '.mc-collab-commit-dialog-textarea'
  };

  WorkspaceFileUserStatus: typeof WorkspaceFileUserStatus = WorkspaceFileUserStatus;

  newCommitForm: FormGroup;
  savingState: LoadingState<string> = new LoadingState<string>();
  workspaceFiles$: Observable<WorkspaceFile[]>;

  get requirePromptOnClose(): boolean {
    return this.newCommitForm?.dirty;
  }

  constructor(
    protected dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: CollabCommitDialogData,
    protected dialogRef: MatDialogRef<CollabCommitDialogComponent>,
    private collabService: CollabService,
    private currentService: CurrentService,
    private errorService: ErrorService,
    private formBuilder: FormBuilder,
    private workspaceFilesService: WorkspaceFilesService
  ) {
    super(dialog, dialogRef);
  }

  ngOnInit() {
    this.workspaceFiles$ = of(this.data.filePaths).pipe(
      switchMap(filePaths => {
        return this.workspaceFilesService.getItemsByProperties$('projectId', this.data.projectId, 'branchName', this.data.branchName).pipe(
          map(workspaceFiles => {
            return workspaceFiles.filter(workspaceFile => {
              return filePaths.some(filePath => workspaceFile.filePath === filePath);
            });
          })
        );
      })
    );

    super.ngOnInit();
    this.buildForm();
  }

  onSubmit() {
    if (!this.newCommitForm.valid) {
      return;
    }

    const currentUser = this.currentService.getLicenseUser();

    this.savingState.update(true);

    this.collabService.commitCollabDocs$({
      branchName: this.data.branchName,
      clientId: this.data.clientId,
      filePaths: this.data.filePaths,
      fileType: CollabFileType.Workspace,
      projectId: this.data.projectId,
      message: this.newCommitForm.controls.commit.value,
      userEmail: currentUser?.User?.Email,
      userId: this.data.userId,
      userName: currentUser.User.FullName
    }).subscribe(result => {
      this.savingState.update(false);

      // If the file was merged (but not fully resolved) then the commit was canceled and the user needs to review the changes
      if (result.type === 'merge') {
        this.closeDialog(null, true);

        this.dialog.open<CollabConflictResolutionDialogComponent, CollabConflictResolutionDialogData>(CollabConflictResolutionDialogComponent, {
          ...CollabConflictResolutionDialogComponent.DialogConfig,
          data: {
            action: 'Commit',
            branchName: this.data.branchName,
            conflictResolutionResult: result.conflictResolutionResult,
            licenseId: this.data.licenseId,
            projectId: this.data.projectId
          }
        });
      } else {
        this.closeDialog(true, true);
      }
    }, error => {
      this.savingState.update(false, 'Unable to commit the changes.', this.errorService.getErrorMessages(error))
    });
  }

  private buildForm() {
    this.newCommitForm = this.formBuilder.group({
      'commit': new FormControl('', [Validators.required, Validators.maxLength(MaxCommitMessageLength), McValidators.notOnlyWhitespace])
    }, {
      updateOn: 'submit'
    });
  }
}
